// Shared marketing data: pricing plans, comparison table, faqs (localized: es primary, en secondary)

export const PLANS = {
  en: {
    basic: {
      id: "basic",
      name: "Basic",
      crc: "₡15,000",
      usd: "~$30",
      tagline: "For independent stylists & single-location studios",
      highlight: false,
      limits: ["1 location", "Up to 3 staff members"],
      features: [
        "Appointment Calendar",
        "Online Booking",
        "Customer CRM & History",
        "Payments, Invoices & Receipts",
        "Reports & Basic Analytics",
        "Inventory",
        "WhatsApp & Email Notifications",
        "Marketing Promotions",
        "Receipt Export (Excel, CSV, PDF)",
        "Loyalty Program & Gift Cards",
        "Role Management",
        "Secure Cloud Backup",
      ],
      excluded: ["No AI features", "No multi-location", "No advanced automation"],
    },
    elite: {
      id: "elite",
      name: "Elite",
      crc: "₡25,000",
      usd: "~$50",
      tagline: "The full AI operating system, unlimited everything",
      highlight: true,
      limits: ["Unlimited locations", "Unlimited staff, customers & storage"],
      intro: "Everything in Basic, plus the full AI suite:",
      features: [
        "AI Receptionist",
        "AI Scheduler & Smart Scheduling",
        "AI Business Coach",
        "AI Revenue Forecasting & Insights",
        "AI Marketing & Promotions Generator",
        "AI WhatsApp Campaign Generator",
        "AI Customer Retention & Rebooking",
        "AI Customer Segmentation",
        "AI Inventory Suggestions",
        "AI Social Content Generator",
        "Workflow Automation & n8n",
        "Advanced Analytics & Executive Reports",
        "Multi-location Dashboard",
        "White Label & Custom Branding",
        "API Access & Priority Support",
        "All future AI features",
      ],
      excluded: [],
    },
  },
  es: {
    basic: {
      id: "basic",
      name: "Basic",
      crc: "₡15,000",
      usd: "~$30",
      tagline: "Para estilistas independientes y estudios de un solo local",
      highlight: false,
      limits: ["1 local", "Hasta 3 miembros del personal"],
      features: [
        "Calendario de citas",
        "Reservas en línea",
        "CRM e historial de clientes",
        "Pagos, facturas y recibos",
        "Reportes y analítica básica",
        "Inventario",
        "Notificaciones por WhatsApp y email",
        "Promociones de marketing",
        "Exportar recibos (Excel, CSV, PDF)",
        "Programa de lealtad y gift cards",
        "Gestión de roles",
        "Respaldo seguro en la nube",
      ],
      excluded: ["Sin funciones de IA", "Sin multi-local", "Sin automatización avanzada"],
    },
    elite: {
      id: "elite",
      name: "Elite",
      crc: "₡25,000",
      usd: "~$50",
      tagline: "El sistema operativo con IA completo, todo ilimitado",
      highlight: true,
      limits: ["Locales ilimitados", "Personal, clientes y almacenamiento ilimitados"],
      intro: "Todo lo de Basic, más la suite completa de IA:",
      features: [
        "Recepcionista IA",
        "Agendador IA y agenda inteligente",
        "Coach de negocio IA",
        "Pronóstico de ingresos e insights con IA",
        "Generador de marketing y promociones IA",
        "Generador de campañas de WhatsApp IA",
        "Retención y recompra de clientes con IA",
        "Segmentación de clientes con IA",
        "Sugerencias de inventario con IA",
        "Generador de contenido social con IA",
        "Automatización de flujos y n8n",
        "Analítica avanzada y reportes ejecutivos",
        "Panel multi-local",
        "Marca blanca y personalización",
        "Acceso API y soporte prioritario",
        "Todas las futuras funciones de IA",
      ],
      excluded: [],
    },
  },
};

export const COMPARISON = {
  en: [
    { category: "Booking & Calendar", basic: true, elite: true },
    { category: "Customer CRM", basic: true, elite: true },
    { category: "Payments & Receipts", basic: true, elite: true },
    { category: "Inventory", basic: true, elite: true },
    { category: "Marketing & Promotions", basic: true, elite: true },
    { category: "Reports", basic: true, elite: true },
    { category: "Analytics", basic: "Basic", elite: "Advanced" },
    { category: "AI Assistant Suite", basic: false, elite: true },
    { category: "Workflow Automation", basic: false, elite: true },
    { category: "Multi-location", basic: false, elite: true },
    { category: "Staff Limit", basic: "Up to 3", elite: "Unlimited" },
    { category: "Support", basic: "Standard", elite: "Priority" },
    { category: "Customization / White Label", basic: false, elite: true },
    { category: "API Access", basic: false, elite: true },
    { category: "Security & Cloud Backup", basic: true, elite: true },
  ],
  es: [
    { category: "Reservas y calendario", basic: true, elite: true },
    { category: "CRM de clientes", basic: true, elite: true },
    { category: "Pagos y recibos", basic: true, elite: true },
    { category: "Inventario", basic: true, elite: true },
    { category: "Marketing y promociones", basic: true, elite: true },
    { category: "Reportes", basic: true, elite: true },
    { category: "Analítica", basic: "Básica", elite: "Avanzada" },
    { category: "Suite de asistente IA", basic: false, elite: true },
    { category: "Automatización de flujos", basic: false, elite: true },
    { category: "Multi-local", basic: false, elite: true },
    { category: "Límite de personal", basic: "Hasta 3", elite: "Ilimitado" },
    { category: "Soporte", basic: "Estándar", elite: "Prioritario" },
    { category: "Personalización / Marca blanca", basic: false, elite: true },
    { category: "Acceso API", basic: false, elite: true },
    { category: "Seguridad y respaldo en la nube", basic: true, elite: true },
  ],
};

export const FAQS = [
  {
    q: "What exactly is BellaOS?",
    a: "BellaOS is the complete AI-powered operating system for beauty businesses — bookings, CRM, payments, inventory, marketing and reporting in one elegant platform, with AI agents that automate the busywork.",
  },
  {
    q: "Is BellaOS only for hair salons?",
    a: "No. BellaOS is built for beauty salons, barbershops, luxury spas, nail salons, boutique studios and wellness clinics. The platform adapts to your services, staff and pricing.",
  },
  {
    q: "How does the AI actually help my business?",
    a: "The AI Receptionist answers client questions 24/7, the AI Scheduler fills quiet slots, and the AI Marketing Generator creates promotions and campaigns automatically — saving hours every week while increasing revenue.",
  },
  {
    q: "Can I switch from Basic to Elite later?",
    a: "Absolutely. You can upgrade to Elite at any time to unlock the full AI suite, unlimited staff and multi-location — your data carries over instantly.",
  },
  {
    q: "Do I need technical knowledge to use it?",
    a: "Not at all. BellaOS is designed for salon owners, not engineers. Everything from booking to AI-generated marketing is a few clicks away.",
  },
  {
    q: "Is my business data secure?",
    a: "Yes. Every business runs in a fully isolated, encrypted environment with secure cloud backups and granular role-based permissions.",
  },
];
